import React, { FunctionComponent } from 'react';
import {
  StyleProp, StyleSheet, Text, TextStyle, TouchableOpacity, ViewStyle, View,
} from 'react-native';
import Theme, { ThemeType } from '../../theme';
import { defaultScale } from '../../utils/scaling';

interface IProps {
  icon?: JSX.Element;
  title: string;
  isSelected: boolean;
  theme?: ThemeType;
  width?: number | string;
  height?: number | string;
  maxWidth?: number;
  subTitle?: string;
  style?: StyleProp<ViewStyle>;
  titleStyle?: StyleProp<TextStyle>;
  subTitleStyle?: StyleProp<TextStyle>;
  margin?: number;
  verticalStack?: boolean;
  overrideDarkStyles?: { [key: string]: number | string };
  overrideLightStyles?: { [key: string]: number | string };
  disabled?: boolean;
  onPress(): void;
}

export const ButtonMargin = 10;

export const SelectionContainer: FunctionComponent<IProps> = ({
  icon,
  title,
  subTitle,
  onPress,
  isSelected,
  width = defaultScale(160),
  height = defaultScale(140),
  maxWidth,
  theme = ThemeType.light,
  style,
  titleStyle,
  subTitleStyle = {},
  margin = 0,
  verticalStack = false,
  overrideDarkStyles = null,
  overrideLightStyles = null,
  disabled = false,
}) => {
  const themeColourMapping = {
    [ThemeType.light]: {
      defaultColor: Theme.CHARCOAL,
      selectedColor: Theme.WHITE,
      defaultSubtitleColor: Theme.CHARCOAL,
      selectedSubtitleColor: Theme.WHITE,
      defaultBackgroundColor: Theme.WHITE,
      selectedBackgroundColor: Theme.GREY_DARK,
      disabledBackgroundColor: Theme.GREY_LIGHT,
      disabledColor: Theme.GREY_MEDIUM_LIGHT,
      disabledSubtitleColor: Theme.GREY_MEDIUM_LIGHT,
      defaultBorderColor: Theme.GREY_LIGHT,
      selectedBorderColor: Theme.GREY_DARK,
      borderWidth: 2,
      ...overrideLightStyles,
    },
    [ThemeType.dark]: {
      defaultColor: Theme.GREY_LIGHT,
      selectedColor: Theme.GREY_DARK,
      defaultSubtitleColor: Theme.GREY_LIGHT,
      selectedSubtitleColor: Theme.GREY_DARK,
      defaultBackgroundColor: Theme.BLACK,
      selectedBackgroundColor: Theme.GREY_LIGHT,
      disabledBackgroundColor: Theme.GREY_DARK,
      disabledColor: Theme.GREY_MEDIUM_LIGHT,
      disabledSubtitleColor: Theme.GREY_MEDIUM_LIGHT,
      defaultBorderColor: Theme.GREY_DARK_MEDIUM,
      selectedBorderColor: Theme.GREY_LIGHT,
      borderWidth: 1,
      ...overrideDarkStyles,
    },
  };
  const themeColours = themeColourMapping[theme];
  const disabledOrDefaultColor = disabled ? themeColours.disabledColor : themeColours.defaultColor;
  const color = isSelected
    ? themeColours.selectedColor
    : disabledOrDefaultColor;

  const disabledOrDefaultSubtitleColor = disabled
    ? themeColours.disabledSubtitleColor
    : themeColours.defaultSubtitleColor;
  const subTitleColor = isSelected
    ? themeColours.selectedSubtitleColor
    : disabledOrDefaultSubtitleColor;

  const disabledOrDefaultBackgroundColor = disabled
    ? themeColours.disabledBackgroundColor
    : themeColours.defaultBackgroundColor;
  const backgroundColor = isSelected
    ? themeColours.selectedBackgroundColor
    : disabledOrDefaultBackgroundColor;
  const borderColor = isSelected
    ? themeColours.selectedBorderColor
    : themeColours.defaultBorderColor;
  const borderWidth = isSelected
    ? themeColours.borderWidth
    : themeColours.borderWidth;
  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      style={[styles.container, { margin },
        style,
        {
          height,
          width,
          backgroundColor,
          borderColor,
          borderWidth,
          maxWidth,
        },
      ]}
    >
      {
        !verticalStack
          && (
          <>
            { icon }
            <Text style={[styles.title, { color }, { paddingTop: icon ? defaultScale(2) : 0 }, titleStyle]}>
              {' '}
              {title}
            </Text>
            {
              subTitle
            && (
            <Text style={[styles.title, { color: subTitleColor }, subTitleStyle]}>
              {' '}
              {subTitle}
            </Text>
            )
            }
          </>
          )
      }
      {
        verticalStack
        && (
        <View style={styles.verticalStackContainer}>
          <Text style={[styles.title, { color }, titleStyle]}>
            {' '}
            {title}
          </Text>
          { icon }
        </View>
        )
      }
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    borderRadius: defaultScale(5),
    borderWidth: defaultScale(2),
    borderColor: Theme.GREY_LIGHT,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    ...Theme.textStyle.bodyCopyReg,
    paddingTop: defaultScale(10),
    textAlign: 'center',
  },
  verticalStackContainer: {
    flexDirection: 'row',
    width: '100%',
    alignItems: 'center',
    height: defaultScale(40),
    justifyContent: 'space-between',
  },
});
